// ---------------------------------------------------------------------------
// Author: Vishnuu A
// Scope: AppRationalization — frontend/src/services (authSession.js)
// Date: 2025-12-05
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'portal_auth_session';

// Login responses use an ISO timestamp, while /auth/me returns the token's
// Unix expiry in seconds. Accept both representations so an expired session is
// discarded locally instead of making a guaranteed-to-fail bootstrap request.
const getExpiryTime = (expiresAt) => {
  if (typeof expiresAt === 'number' && Number.isFinite(expiresAt)) {
    return expiresAt < 1e12 ? expiresAt * 1000 : expiresAt;
  }

  if (typeof expiresAt === 'string' && expiresAt.trim()) {
    const numeric = Number(expiresAt);
    if (Number.isFinite(numeric)) {
      return numeric < 1e12 ? numeric * 1000 : numeric;
    }

    const parsed = Date.parse(expiresAt);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

// Function: isAuthSessionExpired
export const isAuthSessionExpired = (session) => {
  const expiryTime = getExpiryTime(session?.expires_at);
  return expiryTime !== null && expiryTime <= Date.now();
};

// Function: getAuthSession
export const getAuthSession = () => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.user) {
      return null;
    }
    if (isAuthSessionExpired(parsed)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

// Function: setAuthSession
export const setAuthSession = (sessionPayload) => {
  if (!sessionPayload?.token || !sessionPayload?.user) {
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessionPayload));
};

// Function: clearAuthSession
export const clearAuthSession = () => {
  sessionStorage.removeItem(STORAGE_KEY);
};

// Function: getAuthToken
export const getAuthToken = () => {
  const session = getAuthSession();
  return session?.token || null;
};

// Function: getAuthUser
export const getAuthUser = () => {
  const session = getAuthSession();
  return session?.user || null;
};

// Function: hasAppPermission
export const hasAppPermission = (appKey) => {
  const user = getAuthUser();
  if (!user) {
    return false;
  }
  if (user.role === 'admin') {
    return true;
  }
  const apps = Array.isArray(user.apps) ? user.apps : [];
  return apps.includes(appKey);
};
